import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Star, Truck, ShieldCheck, RotateCcw, Minus, Plus } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ChatWidget from '../components/ChatWidget';
import { getProduct } from '../lib/api';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import useSEO from '../hooks/useSEO';

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [isAdding, setIsAdding] = useState(false);

  useSEO({
    title: product ? product.name : 'Product',
    description: product ? product.description?.slice(0, 160) : 'NestFlow product details',
  });

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const data = await getProduct(productId);
        setProduct(data);
      } catch (error) {
        console.error('Failed to fetch product:', error);
        navigate('/products');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId, navigate]);

  const handleAddToCart = () => {
    if (!product) return;
    setIsAdding(true);
    addToCart(product, quantity);
    toast.success(`${product.name} added to cart`);
    setTimeout(() => setIsAdding(false), 1200);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="pt-28 pb-16">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12 animate-pulse">
              <div className="aspect-square rounded-2xl bg-[#F5F5F5]" />
              <div className="space-y-4 py-8">
                <div className="h-4 bg-[#F0F0F0] rounded w-1/4" />
                <div className="h-10 bg-[#F0F0F0] rounded w-3/4" />
                <div className="h-4 bg-[#F0F0F0] rounded w-full" />
                <div className="h-4 bg-[#F0F0F0] rounded w-5/6" />
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!product) return null;

  const discount = product.original_price
    ? Math.round(((product.original_price - product.price) / product.original_price) * 100)
    : null;

  return (
    <div className="min-h-screen bg-white" data-testid="product-detail-page">
      <Header />

      <main className="pt-28 pb-20">
        <div className="max-w-6xl mx-auto px-6">
          {/* Back link */}
          <button
            onClick={() => navigate('/products')}
            className="flex items-center gap-2 text-[#888] hover:text-[#1A1A1C] mb-8 transition-colors"
            data-testid="back-to-products"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            Back to Products
          </button>

          {/* Product Layout */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Image */}
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden bg-[#F5F5F5]">
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>
              {discount && (
                <span className="absolute top-4 left-4 bg-[#1A1A1C] text-white text-sm font-semibold px-4 py-1.5 rounded-full">
                  -{discount}% OFF
                </span>
              )}
              {product.is_bundle && (
                <span className="absolute top-4 right-4 bg-[#34C759] text-white text-sm font-semibold px-4 py-1.5 rounded-full">
                  Bundle Deal
                </span>
              )}
            </div>

            {/* Details */}
            <div className="flex flex-col">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#888] mb-2">
                {product.category}
              </span>
              <h1 className="text-3xl sm:text-4xl font-bold text-[#1A1A1C] font-['Manrope'] tracking-tight mb-4" data-testid="product-name">
                {product.name}
              </h1>

              {/* Rating */}
              <div className="flex items-center gap-3 mb-6">
                <div className="flex items-center gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-[#1A1A1C] text-[#1A1A1C]" />
                  ))}
                </div>
                <span className="text-sm text-[#888]">2,500+ reviews</span>
              </div>

              {/* Price */}
              <div className="flex items-center gap-3 mb-6">
                <span className="text-3xl font-bold text-[#1A1A1C]" data-testid="product-price">
                  ${product.price.toFixed(2)}
                </span>
                {product.original_price && (
                  <span className="text-lg text-[#AAA] line-through">
                    ${product.original_price.toFixed(2)}
                  </span>
                )}
                {discount && (
                  <span className="bg-[#1A1A1C] text-white text-xs font-semibold px-3 py-1 rounded-full">
                    Save ${(product.original_price - product.price).toFixed(0)}
                  </span>
                )}
              </div>

              {/* Description */}
              <p className="text-[#555] leading-relaxed mb-8">
                {product.description}
              </p>

              {/* Features */}
              {product.features && product.features.length > 0 && (
                <div className="mb-8">
                  <h3 className="font-semibold text-[#1A1A1C] mb-3">Features</h3>
                  <ul className="space-y-2.5">
                    {product.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-3 text-[#555]">
                        <Check className="w-5 h-5 text-[#1A1A1C] flex-shrink-0" strokeWidth={2} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Bundle items */}
              {product.is_bundle && product.bundle_items.length > 0 && (
                <div className="mb-8 bg-[#FAFAFA] rounded-xl p-5">
                  <h3 className="font-semibold text-[#1A1A1C] mb-3">Includes</h3>
                  <ul className="space-y-2">
                    {product.bundle_items.map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-[#555]">
                        <Check className="w-4 h-4 text-[#34C759]" strokeWidth={2} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Quantity + Add to Cart */}
              <div className="flex items-center gap-4 mb-6">
                <div className="flex items-center border border-[#E5E5E5] rounded-full">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-3 hover:bg-[#F5F5F5] rounded-l-full transition-colors"
                    data-testid="decrease-quantity"
                    aria-label="Decrease quantity"
                  >
                    <Minus className="w-4 h-4 text-[#555]" strokeWidth={2} />
                  </button>
                  <span className="px-5 font-semibold text-[#1A1A1C] min-w-[3rem] text-center" data-testid="quantity-display">
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="p-3 hover:bg-[#F5F5F5] rounded-r-full transition-colors"
                    data-testid="increase-quantity"
                    aria-label="Increase quantity"
                  >
                    <Plus className="w-4 h-4 text-[#555]" strokeWidth={2} />
                  </button>
                </div>

                <button
                  onClick={handleAddToCart}
                  disabled={isAdding}
                  className={`flex-1 py-4 rounded-full font-semibold text-base flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5 ${
                    isAdding
                      ? 'bg-[#34C759] text-white'
                      : 'bg-[#1A1A1C] text-white hover:bg-[#333]'
                  }`}
                  data-testid="add-to-cart-btn"
                >
                  {isAdding ? (
                    <>
                      <Check className="w-5 h-5" strokeWidth={2} />
                      Added!
                    </>
                  ) : (
                    <>
                      Add to Cart — ${(product.price * quantity).toFixed(2)}
                    </>
                  )}
                </button>
              </div>

              {/* Buy Now */}
              <button
                onClick={() => {
                  handleAddToCart();
                  setTimeout(() => navigate('/cart'), 300);
                }}
                className="w-full py-4 rounded-full font-semibold text-base border-2 border-[#1A1A1C] text-[#1A1A1C] hover:bg-[#1A1A1C] hover:text-white transition-all"
                data-testid="buy-now-btn"
              >
                Buy Now
              </button>

              {/* Trust badges */}
              <div className="grid grid-cols-3 gap-4 mt-8 pt-8 border-t border-[#F0F0F0]">
                {[
                  { icon: <Truck className="w-5 h-5" strokeWidth={1.5} />, text: 'Fast Shipping' },
                  { icon: <ShieldCheck className="w-5 h-5" strokeWidth={1.5} />, text: 'Secure Checkout' },
                  { icon: <RotateCcw className="w-5 h-5" strokeWidth={1.5} />, text: 'Easy Returns' },
                ].map((badge, i) => (
                  <div key={i} className="flex flex-col items-center gap-1.5 text-center">
                    <div className="text-[#1A1A1C]">{badge.icon}</div>
                    <span className="text-xs text-[#888]">{badge.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <ChatWidget />
    </div>
  );
};

export default ProductDetailPage;
